// packages/core/src/learning/school-modules/income-expansion.ts
// ─────────────────────────────────────────────────────────────────────────
// Lens Pack for "Income expansion". surface: 'school',
// homeSchool: 'income-expansion'. module id 'school-income-expansion' matches
// the exhibit's nativeModuleId.
//
// emoji + accentColor are PLACEHOLDERS (accentColor from SchoolMeta color
// #e0712f) — flag for the presentation-tuning pass.
// tier/pillar: income-domain lens (SchoolMeta bucket 'income-life-domains') →
// tier 'growth', pillar 'income-career-lever' (core module 08, Income Is a
// Lever). Same mapping as the sibling career-capital / human-capital schools.
// ─────────────────────────────────────────────────────────────────────────

import type { LearningModule } from '../LearningTypes';

export const SCHOOL_INCOME_EXPANSION_MODULE: LearningModule = {
	id: 'school-income-expansion',
	title: 'Income expansion',
	description:
		'The lens that treats earning power as the top-line lever — raises, skills, negotiation, and side-income tests — aimed at buying options, while staying honest about the cost.',
	emoji: '📈', // PLACEHOLDER — tune in presentation pass
	accentColor: '#e0712f', // from SchoolMeta color — tune in presentation pass
	tier: 'growth',
	pillar: 'income-career-lever',
	order: 0,
	surface: 'school',
	homeSchool: 'income-expansion',
	lessons: [
		{
			id: 'school-income-expansion-01',
			title: 'Cutting has a ceiling',
			description:
				'Learn why Income expansion sees earning power as a real lever, not a bonus topic.',
			pillar: 'income-career-lever',
			schoolsOfThought: ['income-expansion'],
			estimatedReadMinutes: 4,
			estimatedListenMinutes: 5,
			difficulty: 2,
			tags: ['income-upside', 'cutting-ceiling', 'option-growth'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: ['income upside', 'cutting ceiling', 'option growth'],
			blocks: [
				{
					type: 'paragraph',
					text: 'Cutting expenses matters, but it has a ceiling. You can cancel subscriptions, cook more, shop smarter, and still hit a wall if the income side is too small.',
				},
				{
					type: 'paragraph',
					text: 'Income expansion believes the top line deserves attention. More income can create room for debt payoff, savings, investing, health care, moving, learning, family support, and rest. It is not just about having more stuff.',
				},
				{
					type: 'bullet_list',
					items: [
						'Cutting protects the floor',
						'Earning more raises the ceiling',
						'The strongest plan often needs both',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is treating income like a fixed number and spending like the only thing you control.',
				},
				{
					type: 'example',
					text: 'If someone earns $3,200 per month and can save $150, they may feel trapped. Cutting another $50 helps. But a raise, job move, or side income that adds $500 per month changes the whole picture. Now the question is not only what can I remove, but what can I build.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that income growth is not free. It can take time, risk, energy, rejection, training, awkward conversations, and failed attempts. It also depends on the market, your situation, your health, and the opportunities around you.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens belongs in Simulate. Test what higher income could change, then stay honest about what it might cost. A good income move should expand your options without pretending you have unlimited energy.',
				},
				{
					type: 'key_takeaway',
					text: 'Cutting controls the leak, but income expansion can change the size of the bucket.',
				},
			],
			audioScript:
				'Income expansion starts with a point that sounds obvious, but a lot of money advice avoids saying it clearly: cutting expenses has a ceiling. You can spend less. You can get disciplined. You can stop wasting money on random stuff. That matters. But you cannot cut below zero. At some point, the budget is already tight, the easy cuts are gone, and the real problem is not that you bought coffee. The real problem is that the income side is too small for the life you are trying to build.\n\nThis is why Income expansion is such an important lens. It does not say budgeting is useless. It says budgeting is incomplete if it never asks how your earning power could grow. Cutting protects your floor. Earning more raises your ceiling. You need the floor because chaos ruins plans. But you also need the ceiling because dreams, options, and freedom usually require surplus.\n\nThink about someone making $3,200 per month after taxes. They work hard, pay bills, and manage to save $150. That is not nothing. That matters. But if rent, food, transportation, debt, and life keep eating most of the income, the plan can feel painfully slow. Now imagine that same person adds $500 per month through a raise, a better job, overtime, freelance work, a certification, a stronger sales skill, or a small business test. The whole picture changes. Debt payoff speeds up. Runway grows. Stress drops a little. Choices appear that did not feel real before.\n\nThat is the heart of this school. Income is not just money. Income is optionality. It can give you room to breathe, room to say no, room to move, room to recover, room to invest, and room to take smarter risks. But the honest version of this lens has to name the cost. More income is not magic. It may require uncomfortable conversations. It may require learning after work. It may require applying and getting rejected. It may require building proof before anyone pays you more. It may require a side project that fails before one works.\n\nThere is also a fairness issue. Not everyone has the same runway, health, safety, schedule, support, education, or network. Income expansion becomes shallow when it acts like effort alone fixes everything. It does not. But the useful version says: within the reality you have, where is there upside? What skill could matter? What ask could be made? What job market could be explored? What small income test could teach you something?\n\nInside Nisk, this is not about hyping yourself into a random leap. It is about testing the tradeoff. Open Simulate and ask what an extra $300, $500, or $1,000 per month would change. Then ask what it would realistically cost to pursue. That is the discipline. Do not worship cutting. Do not worship earning. Use both.',
			flashcards: [
				{
					id: 'school-income-expansion-01-fc-1',
					role: 'main_idea',
					front: 'What is the cutting ceiling?',
					back: 'Expenses can only be reduced so far, but income can sometimes expand.',
				},
				{
					id: 'school-income-expansion-01-fc-2',
					role: 'watch_out',
					front: 'What is the income trap?',
					back: 'Acting like more income has no cost, risk, or energy demand.',
				},
				{
					id: 'school-income-expansion-01-fc-3',
					role: 'use_it',
					front: 'How do you apply this lens?',
					back: 'Test what higher income could change, then count the tradeoff honestly.',
				},
			],
			applyAction: {
				type: 'openSimulator',
				label: 'Simulate higher income',
				scenarioType: 'side-hustle',
			},
			revision: 1,
		},
		{
			id: 'school-income-expansion-02',
			title: 'Skills create income options',
			description:
				'Learn why Income expansion cares about useful skills, proof, and market demand.',
			pillar: 'income-career-lever',
			schoolsOfThought: ['income-expansion'],
			estimatedReadMinutes: 4,
			estimatedListenMinutes: 5,
			difficulty: 2,
			tags: ['marketable-skill', 'proof-building', 'income-ladder'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: ['marketable skill', 'proof building', 'income ladder'],
			blocks: [
				{
					type: 'paragraph',
					text: 'Wanting more income is not enough. Income expansion asks what ability could make someone willing to pay you more.',
				},
				{
					type: 'paragraph',
					text: 'This lens sees skills as income options. A skill is not valuable only because you like it. It becomes powerful when it solves a problem, creates value, reduces pain, saves time, increases sales, improves systems, or makes someone’s life easier.',
				},
				{
					type: 'bullet_list',
					items: [
						'Learn something useful',
						'Prove you can use it',
						'Put it near people who pay for it',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is collecting information instead of building proof.',
				},
				{
					type: 'example',
					text: 'Two people want better pay. One watches career videos for months. The other builds a small portfolio, documents results at work, practices interviews, and asks for feedback. Both care. Only one is creating evidence.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that skill building can be slow and uneven. Some skills pay faster than others. Some markets are crowded. Some people need stability before they can take on more learning.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens should push a clean question: what skill could raise your earning power without wrecking your life? The answer should be practical, testable, and connected to your real numbers.',
				},
				{
					type: 'key_takeaway',
					text: 'Income growth becomes more realistic when skill turns into visible proof.',
				},
			],
			audioScript:
				'Income expansion sounds exciting when people talk about making more money, but the serious version is not hype. It asks a practical question: what can you do that someone values enough to pay for? That is where skills come in.\n\nA skill is not just a certificate. It is not just a course you watched. It is not just something you wrote on a resume. A real skill is an ability that creates value in the world. It solves a problem. It saves time. It reduces mistakes. It brings in customers. It organizes chaos. It improves a process. It helps a team move faster. It makes a customer trust you. It helps a business earn, protect, or keep money. That is why skills matter to income expansion. They are not motivational decorations. They are income options.\n\nThe problem is that people often confuse learning with proof. Learning is necessary, but proof is what changes opportunities. If you want to move into a better role, ask for a raise, freelance, sell a service, or start a side income stream, someone usually needs evidence. They need to see that you can do the thing. That evidence might be a project, a work result, a portfolio, a case study, a referral, a sales record, a certification with real practice behind it, or a clear story of how you solved a problem.\n\nPicture two people who both want to earn more. One spends months watching videos about high income skills. They feel busy, but nothing visible changes. The other chooses one skill, practices it, builds a small example, gets feedback, improves it, and shows it to someone who might care. The second person is still not guaranteed anything. But they are creating proof. Proof makes a conversation stronger. Proof makes a resume stronger. Proof makes an interview stronger. Proof makes a negotiation less vague.\n\nIncome expansion also has to stay honest. Not every skill pays quickly. Not every market rewards effort fairly. Some people have less time because of family, health, commuting, caregiving, or survival pressure. Some people are starting from a place where stability matters more than growth. A good income lens does not shame that. It asks for the next right skill bet inside your actual life.\n\nThe skill does not have to be glamorous. It might be better communication. It might be sales. It might be coding. It might be data work. It might be trade work. It might be project coordination. It might be writing clear proposals. It might be learning how to manage clients. The question is not, “What sounds impressive?” The question is, “What ability could make my income options less fragile?”\n\nInside Nisk, this connects your skill plan to your numbers. Look at your Dashboard and see how much room you have. Use Simulate to test what more income could change. Then work backward. What proof would make that income move more believable? That is a grounded way to pursue upside.',
			flashcards: [
				{
					id: 'school-income-expansion-02-fc-1',
					role: 'main_idea',
					front: 'Why do skills matter?',
					back: 'Useful skills can create income options when they solve problems people value.',
				},
				{
					id: 'school-income-expansion-02-fc-2',
					role: 'watch_out',
					front: 'What is fake skill building?',
					back: 'Consuming information without creating proof of ability.',
				},
				{
					id: 'school-income-expansion-02-fc-3',
					role: 'use_it',
					front: 'What proof should you build?',
					back: 'A project, result, portfolio, case study, or clear work outcome.',
				},
			],
			applyAction: {
				type: 'noToolAction',
				label: 'Choose a skill proof',
				prompt: 'Write one skill you want to build and one proof piece you could create within thirty days.',
			},
			revision: 1,
		},
		{
			id: 'school-income-expansion-03',
			title: 'Negotiation is an income lever',
			description:
				'Learn why Income expansion treats asking well as a skill, not a personality trait.',
			pillar: 'income-career-lever',
			schoolsOfThought: ['income-expansion'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 3,
			tags: ['negotiation-skill', 'value-evidence', 'ask-timing'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: ['negotiation skill', 'value evidence', 'ask timing'],
			blocks: [
				{
					type: 'paragraph',
					text: 'A lot of people lose money because they never ask. Not because they are lazy. Because asking feels awkward, risky, or selfish.',
				},
				{
					type: 'paragraph',
					text: 'Income expansion sees negotiation as a learnable income lever. A better ask can affect not just one paycheck, but every paycheck after it. The lens is not about being pushy. It is about making value visible and choosing the right moment.',
				},
				{
					type: 'bullet_list',
					items: [
						'Know what value you created',
						'Know what the market pays',
						'Ask clearly and calmly',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is waiting for someone to notice your value without being shown.',
				},
				{
					type: 'example',
					text: 'A worker improves a process, trains two new teammates, and handles a larger workload. If they say nothing, the company may accept the extra value as normal. If they document results and ask at review time, the conversation changes.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that negotiation does not always work. Some workplaces are rigid. Some managers are unfair. Some markets are weak. Sometimes the real negotiation is choosing whether to stay, move, or build options elsewhere.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens connects to runway. The more breathing room you have, the less desperate the ask feels. Runway does not guarantee a yes, but it can make you calmer and less trapped.',
				},
				{
					type: 'key_takeaway',
					text: 'Negotiation is not begging for more. It is making value, timing, and options visible.',
				},
			],
			audioScript:
				'Negotiation is one of the most underrated income levers because it feels personal. People hear “negotiate” and imagine being aggressive, awkward, greedy, or confrontational. But Income expansion sees it differently. Negotiation is not about becoming someone fake. It is about learning how to make value visible and ask with structure.\n\nA lot of people work hard and hope someone notices. Sometimes they do. Often they do not. Businesses get busy. Managers get distracted. Pay systems are slow. Good work can become invisible if it is not translated into results. That is why negotiation matters. If your work saves time, improves quality, helps customers, supports a team, reduces errors, trains people, or creates revenue, that value needs language. Otherwise it may never enter the pay conversation.\n\nThis lens believes an ask gets stronger when it has evidence. “I want more money” is honest, but weak by itself. “Here are the responsibilities I took on, here are the results, here is the market range I am seeing, and I would like to discuss adjusting my compensation” is different. It is still not a guarantee. But it is clearer. It treats the conversation like a business discussion instead of a secret wish.\n\nNegotiation also compounds. A raise does not only affect one month. It can affect every future month, every percentage based increase, every job offer anchored to your current pay, and every savings decision built on that income. That is why one strong ask can matter more than dozens of tiny cuts. Again, this does not mean cutting is useless. It means income deserves respect as a lever.\n\nNow the honest part. Negotiation does not always work. Some employers are rigid. Some managers are weak advocates. Some jobs have fixed bands. Some companies will take your extra work and still say no. Some people face bias, unstable industries, or power dynamics that make asking harder. Income expansion should not pretend every no is your fault. Sometimes the answer is not to ask better. Sometimes the answer is to build options elsewhere.\n\nThat is where runway matters. When you have no breathing room, every money conversation feels loaded. You may accept less because you feel trapped. You may avoid asking because you cannot handle the stress of a bad outcome. Runway does not make you fearless, but it can make you steadier. It gives you space to ask, apply, compare, learn, and walk away if needed.\n\nInside Nisk, use this lesson to connect negotiation to your real life. Check your runway. Look at your current income and expenses. Ask what a five percent, ten percent, or modest monthly increase would change. Then ask what proof you would need to make a serious case. Negotiation is not magic. It is a skill. And like any skill, it gets better when you practice before the stakes are huge.',
			flashcards: [
				{
					id: 'school-income-expansion-03-fc-1',
					role: 'main_idea',
					front: 'Why does negotiation matter?',
					back: 'A better ask can raise future income, not just one paycheck.',
				},
				{
					id: 'school-income-expansion-03-fc-2',
					role: 'watch_out',
					front: 'What weakens an ask?',
					back: 'Asking without evidence, timing, market awareness, or options.',
				},
				{
					id: 'school-income-expansion-03-fc-3',
					role: 'use_it',
					front: 'What should you prepare?',
					back: 'Results, responsibilities, market data, and a clear request.',
				},
			],
			applyAction: {
				type: 'calculateRunway',
				label: 'Check your runway',
			},
			revision: 1,
		},
		{
			id: 'school-income-expansion-04',
			title: 'Side income is a test',
			description:
				'Learn why Income expansion sees side income as experimentation, not instant escape.',
			pillar: 'income-career-lever',
			schoolsOfThought: ['income-expansion'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 3,
			tags: ['income-experiment', 'paid-demand', 'time-cost'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: ['income experiment', 'paid demand', 'time cost'],
			blocks: [
				{
					type: 'paragraph',
					text: 'Side income gets sold like freedom, but it can also become a second job with worse boundaries. Income expansion treats it as a test, not a fantasy.',
				},
				{
					type: 'paragraph',
					text: 'This lens believes side income is useful when it teaches what people will pay for, what you can deliver, and what the real cost is. The goal is not to look busy. The goal is to learn with small stakes.',
				},
				{
					type: 'bullet_list',
					items: [
						'Test demand before scaling',
						'Count time and stress',
						'Keep the experiment small enough to survive',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is counting revenue while ignoring exhaustion.',
				},
				{
					type: 'example',
					text: 'Someone makes $400 from a weekend service. That sounds great until they realize it took twenty hours, caused missed sleep, and left them behind at work. Another person makes $200 in five focused hours and learns there is repeat demand. The smaller number may be the better signal.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that side income can distract from the main income path. Sometimes the best move is a raise, a job switch, or a skill upgrade, not another gig.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, test side income through Simulate and reflection. Ask what the money changes, what the time costs, and whether the experiment teaches something useful.',
				},
				{
					type: 'key_takeaway',
					text: 'Side income is strongest when it is a controlled experiment, not a desperate escape hatch.',
				},
			],
			audioScript:
				'Side income is one of those ideas that sounds simple from the outside. Make extra money. Use it to pay debt, save faster, invest, build runway, or create freedom. That can be real. But Income expansion wants you to look at the whole picture, not just the extra cash.\n\nA side income stream is not automatically good. It depends on what it costs. It costs time. It costs energy. It can cost sleep, relationships, focus, weekends, recovery, and attention. It can also create tax complexity, inconsistent income, customer stress, or pressure to perform after your main job already drained you. That does not mean side income is bad. It means the test has to be honest.\n\nThe best version of side income is experimentation. You are testing demand. Will someone pay for this? Can I deliver it well? Do I enjoy the work enough to repeat it? Is the hourly return worth it? Does this build a skill? Could this become a better opportunity later? Does this fit my life right now? Those are sharper questions than “How do I make money fast?”\n\nImagine someone earns $400 in a weekend doing a service. On the surface, that sounds like a win. But maybe it took twenty hours, created stress, ruined sleep, and made Monday worse. Now compare that with someone who earns $200 in five focused hours doing something they can repeat, improve, and eventually charge more for. The first person earned more cash that weekend. The second person may have found a better signal.\n\nIncome expansion is always looking for upside, but it should not ignore the main path. Sometimes a side hustle is not the best income move. Maybe your current job has more upside if you negotiate. Maybe switching companies would change more than weekend gigs. Maybe one certification, portfolio, or sales skill would pay more over time. Maybe your energy is already too low, and adding more work would damage the asset you are trying to grow.\n\nThe danger is using side income as emotional escape. You hate your job, feel behind, or want freedom, so you add more work without strategy. That can make life feel even tighter. A better approach is to keep the experiment small. Define what you are testing. Set a time limit. Track money, hours, stress, and learning. Decide whether to continue based on evidence, not fantasy.\n\nInside Nisk, use Simulate to test what extra monthly income would change. Then add the missing question: what would it cost to earn that money? If an extra $500 per month changes your timeline, that is useful. But if earning it destroys your health or focus, the tradeoff may not be worth it. Income expansion is not about chasing every dollar. It is about finding income that expands options instead of quietly shrinking your life.',
			flashcards: [
				{
					id: 'school-income-expansion-04-fc-1',
					role: 'main_idea',
					front: 'What is side income?',
					back: 'A small test of demand, delivery, time cost, and learning.',
				},
				{
					id: 'school-income-expansion-04-fc-2',
					role: 'watch_out',
					front: 'What gets ignored?',
					back: 'Stress, sleep, taxes, focus, and the real hourly cost.',
				},
				{
					id: 'school-income-expansion-04-fc-3',
					role: 'use_it',
					front: 'How should you test it?',
					back: 'Keep it small, track hours, and judge the signal honestly.',
				},
			],
			applyAction: {
				type: 'openSimulator',
				label: 'Test side income',
				scenarioType: 'side-hustle',
			},
			revision: 1,
		},
		{
			id: 'school-income-expansion-05',
			title: 'More income needs a purpose',
			description:
				'Learn why Income expansion works best when extra money is aimed at options, not lifestyle drift.',
			pillar: 'income-career-lever',
			schoolsOfThought: ['income-expansion'],
			estimatedReadMinutes: 4,
			estimatedListenMinutes: 5,
			difficulty: 2,
			tags: ['income-purpose', 'lifestyle-drift', 'surplus-direction'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'purpose for income',
				'lifestyle drift',
				'surplus direction',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'More income can change your life, or it can disappear quietly. Income expansion is powerful, but only if the extra money has a job.',
				},
				{
					type: 'paragraph',
					text: 'This lens believes earning more should connect to options: runway, debt relief, investing, education, mobility, family support, health, or freedom. Without direction, higher income can become higher spending with better receipts.',
				},
				{
					type: 'bullet_list',
					items: [
						'Name the purpose',
						'Capture part of the increase',
						'Let some money improve life',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is letting every raise become normal before it builds anything.',
				},
				{
					type: 'example',
					text: 'Someone gets a $600 monthly increase. If all of it disappears into upgrades, their stress may stay the same. If $300 goes to runway, $150 improves life, and $150 goes toward a goal, the raise starts buying options.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that not every dollar needs to be optimized. People need joy, comfort, and relief. The point is not to punish yourself for earning more. The point is to stop income growth from being swallowed by default.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, use Dashboard and Simulate to decide what new income is for. The question is not just how do I earn more. The question is what does more income buy back?',
				},
				{
					type: 'key_takeaway',
					text: 'More income matters most when it turns into more options, not just a more expensive normal.',
				},
			],
			audioScript:
				'Income expansion can be powerful, but it has one huge weakness: more money can disappear. Not because you are stupid. Because life adapts. Your standards rise. Your friends spend differently. Your old stress gets replaced by new expectations. Convenience becomes normal. Upgrades stop feeling like upgrades. A raise that should have created breathing room quietly becomes a new baseline.\n\nThat is why Income expansion needs purpose. More income is not automatically freedom. More income only becomes freedom when some of it is directed toward options. That could mean debt relief, emergency runway, investing, skill building, health, moving, supporting family, starting over, taking a safer risk, or simply reducing pressure. The point is to decide before the money gets absorbed.\n\nThis lens is not anti spending. That matters. If you earn more, it is normal to want life to feel better. Maybe you upgrade groceries. Maybe you fix your car. Maybe you buy clothes that fit. Maybe you go out sometimes. Maybe you stop living with constant tension. That is not failure. Money is supposed to improve life. The problem is when every dollar of new income becomes invisible lifestyle drift and none of it buys back options.\n\nTake a simple example. Someone gets a $600 per month increase after taxes. If they do nothing intentional, that money may vanish into food delivery, nicer purchases, subscriptions, random upgrades, and social spending. Their life may feel slightly better, but their freedom path does not change much. Now imagine they decide ahead of time: $300 goes to runway, $150 goes toward a goal, and $150 is for life improvement. That person is not being extreme. They are giving the raise a job.\n\nThe exact split is not the point. Nisk is not here to prescribe a personal formula. The point is awareness. When income rises, ask what the increase is meant to do. Is it buying stability? Is it buying time? Is it buying learning? Is it buying health? Is it buying freedom from debt pressure? Is it buying a better living situation? Is it buying a little joy so the plan feels livable?\n\nThe hidden cost of not deciding is that the world decides for you. Apps, ads, friends, habits, stress, and convenience will happily spend the increase. That does not make you weak. It means defaults are powerful. Income expansion works best when you set a better default before the money arrives.\n\nInside Nisk, this is where Dashboard and Simulate work together. Dashboard shows where the extra money could land. Simulate shows what it could change over time. Reflection helps you decide what the money is for. More income is not the finish line. It is raw material. The real win is turning it into options.',
			flashcards: [
				{
					id: 'school-income-expansion-05-fc-1',
					role: 'main_idea',
					front: 'Why does income need purpose?',
					back: 'Extra money can disappear unless it is aimed at options.',
				},
				{
					id: 'school-income-expansion-05-fc-2',
					role: 'watch_out',
					front: 'What is lifestyle drift?',
					back: 'Letting higher income become higher spending without a plan.',
				},
				{
					id: 'school-income-expansion-05-fc-3',
					role: 'use_it',
					front: 'What should a raise do?',
					back: 'Improve life while also building runway, goals, or freedom.',
				},
			],
			applyAction: {
				type: 'openDashboard',
				label: 'Decide where extra income goes',
			},
			revision: 1,
		},
	],
};
